import { Button } from "@/components/ui/button";
import {
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { MAX_PINNED_PROMPTS } from "@/enums/pinned-prompts";
import { useTranslation } from "@/hooks/useTranslation";
import { PinnedContext } from "@shaarp/common";
import { LayoutGrid, MessageSquare, Pin, Star } from "lucide-react";
import { LuPlus, LuSearch } from "react-icons/lu";

type PromptLibraryHeaderProps = {
  pinnedCount: number;
  favoriteCount: number;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onCreateClick: () => void;
  isMobile?: boolean;
  context?: PinnedContext;
};

export function PromptLibraryHeader({
  pinnedCount,
  favoriteCount,
  searchQuery,
  onSearchChange,
  onCreateClick,
  isMobile,
  context,
}: PromptLibraryHeaderProps) {
  const { t } = useTranslation();

  return (
    <DialogHeader className="shrink-0 px-6 pt-6 pb-3">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <DialogTitle className="text-xl font-semibold">
              {t("prompt.library.title")}
            </DialogTitle>
            {context && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 text-sm font-medium rounded-full bg-blue-500/10 text-blue-700 dark:text-blue-400">
                <Pin className="w-3.5 h-3.5 fill-current" />
                <span>{t("prompt.pinned.pinnedFor")}</span>
                {context === "company" ? (
                  <LayoutGrid className="w-3.5 h-3.5" />
                ) : (
                  <MessageSquare className="w-3.5 h-3.5" />
                )}
                <span>
                  {t(`prompt.pinned.context.${context}`)} ({pinnedCount}/{MAX_PINNED_PROMPTS[context]})
                </span>
              </div>
            )}
            {!context && pinnedCount > 0 && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 text-sm font-medium rounded-full bg-blue-500/10 text-blue-700 dark:text-blue-400">
                <Pin className="w-3.5 h-3.5 fill-current" />
                <span>
                  {pinnedCount}
                </span>
              </div>
            )}
            {favoriteCount > 0 && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 text-sm font-medium rounded-full bg-yellow-500/10 text-yellow-500 dark:text-yellow-600">
                <Star className="w-3.5 h-3.5 fill-current" />
                <span>{favoriteCount}</span>
              </div>
            )}
          </div>
          {!isMobile && (
            <DialogDescription className="text-muted-foreground">
              {t("prompt.library.description")}
            </DialogDescription>
          )}
        </div>
        {!isMobile && (
          <div className="flex items-center gap-2">
            <div className="relative w-64">
              <LuSearch className="absolute -translate-y-1/2 left-3 top-1/2 size-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder={t("common.search")}
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button onClick={onCreateClick} type="submit">
              <LuPlus className="mr-2 size-4" />
              {t("prompt.select.buttons.createPrompt")}
            </Button>
          </div>
        )}
      </div>
      {isMobile && (
        <div className="flex items-center gap-2 mt-3">
          <div className="relative flex-1">
            <LuSearch className="absolute -translate-y-1/2 left-3 top-1/2 size-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t("common.search")}
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button onClick={onCreateClick} type="submit" size="icon">
            <LuPlus className="size-4" />
          </Button>
        </div>
      )}
    </DialogHeader>
  );
}
